import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

import { BASE_URL } from '../services/api';

interface UseWorkCheckpointWebSocketOptions {
    workActive: boolean;
    workId: number | undefined;
    workToken: string | null;
    onSolutionUrlsUpdate: (urls: string[]) => void;
    onReviewStatusUpdate?: (reviewStatus: number, rejectionReason?: string) => void;
    onConnectionStatusChange?: (connected: boolean) => void;
    onWorkflowsUpdate?: (workflowsPassed: boolean, workflowsFeedback?: string) => void;
    onInactivityPing?: (data: { workId: number; pingNumber: number }) => void;
}

/**
 * Custom hook to manage WebSocket connection for work checkpoint updates.
 * NOTES:
 * - Real-time updates via WebSocket for good connections
 * - Automatic retry with exponential backoff for unstable connections
 * - Zero-message-loss: Server stores missed messages in Redis
 * - On reconnect, server automatically pushes any missed messages
 */
export const useWorkCheckpointWebSocket = ({
    workActive,
    workId,
    workToken,
    onSolutionUrlsUpdate,
    onReviewStatusUpdate,
    onConnectionStatusChange,
    onWorkflowsUpdate,
    onInactivityPing
}: UseWorkCheckpointWebSocketOptions): void => {
    const socketRef = useRef<Socket | null>(null);
    
    // Use refs to keep callbacks up to date without causing reconnections
    const callbackRef = useRef(onSolutionUrlsUpdate);
    const reviewStatusCallbackRef = useRef(onReviewStatusUpdate);
    const connectionStatusCallbackRef = useRef(onConnectionStatusChange);
    const workflowsCallbackRef = useRef(onWorkflowsUpdate);
    const inactivityPingCallbackRef = useRef(onInactivityPing);
    
    // Update refs when callbacks change (without triggering WebSocket reconnection)
    callbackRef.current = onSolutionUrlsUpdate;
    reviewStatusCallbackRef.current = onReviewStatusUpdate;
    connectionStatusCallbackRef.current = onConnectionStatusChange;
    workflowsCallbackRef.current = onWorkflowsUpdate;
    inactivityPingCallbackRef.current = onInactivityPing;

    useEffect(() => {
        // Don't connect if work is not active or required data is missing
        if (!workActive || !workId || !workToken) {
            // Clean up socket if work is not active
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            return;
        }

        // Connect to socket.io server
        const socket = io(BASE_URL, {
            path: '/backend-ws/api/v1/work/ws',
            transports: [ 'polling', 'websocket' ],
            auth: {
                token: workToken,
                jwt: workToken
            },
            query: {
                work_id: workId,
                token: workToken
            },
            extraHeaders: {
                'Authorization': `Bearer ${workToken}`
            },
            withCredentials: true, // allow sending cookies
            reconnection: true,
            reconnectionAttempts: Infinity, // Keep trying to reconnect
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000, // Max 10 seconds between attempts
            randomizationFactor: 0.5, // Add jitter to avoid thundering herd
            forceNew: false,
            timeout: 20000 // 20 second connection timeout
        });

        socketRef.current = socket;

        // Wait for connection before joining room
        const roomName = `work_${workId}`;
        
        const handleJoinRoom = () => {
            socket.emit('join', roomName);
        };

        socket.on('connect', () => {
            handleJoinRoom();
            if (connectionStatusCallbackRef.current) {
                connectionStatusCallbackRef.current(true);
            }
        });

        socket.on('connect_error', (error: Error) => {
            console.warn('WebSocket connection error:', error);
            if (connectionStatusCallbackRef.current) {
                connectionStatusCallbackRef.current(false);
            }
        });

        socket.on('disconnect', () => {
            if (connectionStatusCallbackRef.current) {
                connectionStatusCallbackRef.current(false);
            }
        });

        socket.on('reconnect', () => {
            // Join room - server will automatically push any missed messages
            handleJoinRoom();
            if (connectionStatusCallbackRef.current) {
                connectionStatusCallbackRef.current(true);
            }
        });

        socket.on('reconnect_attempt', (attemptNumber: number) => {
            console.debug(`WebSocket reconnection attempt ${attemptNumber}`);
        });

        socket.on('reconnect_failed', () => {
            console.warn('WebSocket reconnection failed - will retry');
        });

        // Listen for work checkpoint updates with acknowledgment
        // The 'ack' callback parameter is provided by Socket.IO when the server expects acknowledgment
        socket.on('work_checkpoint_update', (data: any, ack?: () => void) => {
            const receivedWorkId = data.workId || data.work_id;

            if (receivedWorkId !== workId) {
                // Even if workId doesn't match, acknowledge to prevent server retries
                if (ack) {
                    ack();
                }
                return;
            }

            try {
                // Handle solutionUrls update
                const solutionUrls = data.solutionUrls || (data.solutionUrl ? [ data.solutionUrl ] : []);
                if (solutionUrls && solutionUrls.length > 0) {
                    callbackRef.current(solutionUrls);
                }

                // Handle reviewStatus update (with optional rejectionReason)
                if (data.reviewStatus !== undefined && reviewStatusCallbackRef.current) {
                    reviewStatusCallbackRef.current(data.reviewStatus, data.rejectionReason);
                }

                // Handle workflows update
                if (data.workflowsPassed !== undefined && workflowsCallbackRef.current) {
                    workflowsCallbackRef.current(data.workflowsPassed, data.workflowsFeedback);
                }
                
                // Acknowledge successful receipt to backend
                // This tells the server to clear the message from Redis
                if (ack) {
                    ack();
                }
            } catch (error) {
                console.error('Error processing work checkpoint update:', error);
                // Don't acknowledge if processing failed - let server retry
            }
        });

        socket.on('inactivity_ping', (data: { workId: number; pingNumber: number }) => {
            if (inactivityPingCallbackRef.current) {
                inactivityPingCallbackRef.current(data);
            }
        });

        // Cleanup on unmount or when dependencies change
        return () => {
            if (socketRef.current) {
                socketRef.current.emit('leave', roomName);
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            // Don't clear callback refs - they should persist across reconnections
        };
    }, [ workActive, workId, workToken ]);
};
